import React, { useCallback, useEffect, useState } from "react";
import { Source, Layer, Marker } from "react-map-gl";
import { useAppSelector, useAppDispatch } from "./store/hooks";
import {
    selectClientAssetData,
    selectCurrentDeviceTypeBeingEdited,
    selectCurrentDeviceBeingEdited,
    selectDeviceData,
    selectExistingDeviceIdBeingEdited,
    selectVisibleSpans,
    setTargetStructureForDevice,
} from "./store/reducers/deviceLinkingAppStoreSlice";
import ClientAssetDataMap from "../ClientAssetDataMap";

function generateSpanFeature(span, structures) {
    const startStructure = structures.find((s) => s.structure_name === span.start_structure);
    const endStructure = structures.find((s) => s.structure_name === span.end_structure);

    if (!startStructure || !endStructure) return null;

    return {
        type: "Feature",
        geometry: {
            type: "LineString",
            coordinates: [
                [startStructure.longitude, startStructure.latitude],
                [endStructure.longitude, endStructure.latitude],
            ],
        },
        properties: {
            description: `Span: ${span.start_structure}-${span.end_structure} (${span.section_name})`,
            latitude: span.latitude,
            longitude: span.longitude,
        },
    };
}

function generateGeoJsonArrow(deviceInfo, currentDeviceTypeBeingEdited, size = 0.7, makeClickable = true) {
    const centerPositionLatitude = deviceInfo.position?.latitude;
    const centerPositionLongitude = deviceInfo.position?.longitude;
    const heading = deviceInfo?.anemometer_heading ?? 0;

    if (centerPositionLatitude === undefined || centerPositionLongitude === undefined) {
        return null;
    }

    const ARROW_LENGTH = 0.0005 * size;
    const ARROW_WIDTH = 0.0003 * size;

    const cartesianAngle = -heading;
    const headingRadians = Math.PI * (cartesianAngle / 180.0);

    const tipLatDelta = ARROW_LENGTH;
    const tipLonDelta = 0;
    const baseLeftLatDelta = -ARROW_LENGTH * 0.5;
    const baseLeftLonDelta = -ARROW_WIDTH;
    const baseRightLatDelta = -ARROW_LENGTH * 0.5;
    const baseRightLonDelta = ARROW_WIDTH;
    const baseBackLatDelta = -ARROW_LENGTH * 0.5;
    const baseBackLonDelta = 0;

    const tip = [
        centerPositionLongitude + tipLonDelta * Math.cos(headingRadians) - tipLatDelta * Math.sin(headingRadians),
        centerPositionLatitude + tipLatDelta * Math.cos(headingRadians) + tipLonDelta * Math.sin(headingRadians),
    ];
    const baseLeft = [
        centerPositionLongitude + baseLeftLonDelta * Math.cos(headingRadians) - baseLeftLatDelta * Math.sin(headingRadians),
        centerPositionLatitude + baseLeftLatDelta * Math.cos(headingRadians) + baseLeftLonDelta * Math.sin(headingRadians),
    ];
    const baseBack = [
        centerPositionLongitude + baseBackLonDelta * Math.cos(headingRadians) - baseBackLatDelta * Math.sin(headingRadians),
        centerPositionLatitude + baseBackLatDelta * Math.cos(headingRadians) + baseBackLonDelta * Math.sin(headingRadians),
    ];
    const baseRight = [
        centerPositionLongitude + baseRightLonDelta * Math.cos(headingRadians) - baseRightLatDelta * Math.sin(headingRadians),
        centerPositionLatitude + baseRightLatDelta * Math.cos(headingRadians) + baseRightLonDelta * Math.sin(headingRadians),
    ];

    const deviceDescription =
        currentDeviceTypeBeingEdited === "edge-device"
            ? `Edge Device: ${deviceInfo.edge_device_id}`
            : currentDeviceTypeBeingEdited === "weather-sensor"
            ? `Weather Sensor: ${deviceInfo.device_sn}`
            : "";

    return {
        type: "Feature",
        properties: makeClickable
            ? {
                  description: deviceDescription,
                  latitude: centerPositionLatitude,
                  longitude: centerPositionLongitude,
              }
            : {},
        geometry: {
            coordinates: [[tip, baseRight, baseBack, baseLeft, tip]],
            type: "Polygon",
        },
    };
}

function generateGeoJsonCamera(deviceInfo, currentDeviceTypeBeingEdited, size, makeClickable = true) {
    const centerPositionLatitude = deviceInfo.position?.latitude;
    const centerPositionLongitude = deviceInfo.position?.longitude;
    const heading = deviceInfo?.approx_angle ?? 0;

    if (centerPositionLatitude === undefined || centerPositionLongitude === undefined) {
        return null;
    }

    const CAMERA_BOX_GEOJSON_HEIGHT_LAT = 0.0004;
    const CAMERA_BOX_GEOJSON_WIDTH_LON = 0.0005;

    const cartesianAngle = -heading;
    const headingRadians = Math.PI * (cartesianAngle / 180.0);

    const upperLeftBoxCornerLatDelta = +(CAMERA_BOX_GEOJSON_HEIGHT_LAT / 2) * size;
    const upperLeftBoxCornerLonDelta = -(CAMERA_BOX_GEOJSON_WIDTH_LON / 2) * size;
    const upperRightBoxCornerLatDelta = +(CAMERA_BOX_GEOJSON_HEIGHT_LAT / 2) * size;
    const upperRightBoxCornerLonDelta = +(CAMERA_BOX_GEOJSON_WIDTH_LON / 2) * size;
    const lowerLeftBoxCornerLatDelta = -(CAMERA_BOX_GEOJSON_HEIGHT_LAT / 2) * size;
    const lowerLeftBoxCornerLonDelta = -(CAMERA_BOX_GEOJSON_WIDTH_LON / 2) * size;
    const lowerRightBoxCornerLatDelta = -(CAMERA_BOX_GEOJSON_HEIGHT_LAT / 2) * size;
    const lowerRightBoxCornerLonDelta = +(CAMERA_BOX_GEOJSON_WIDTH_LON / 2) * size;

    const upperLeftLensMountCornerLatDelta = +(CAMERA_BOX_GEOJSON_HEIGHT_LAT / 2) * size;
    const upperLeftLensMountCornerLonDelta = -(CAMERA_BOX_GEOJSON_WIDTH_LON / 2) * size * 0.4;
    const upperRightLensMountCornerLatDelta = +(CAMERA_BOX_GEOJSON_HEIGHT_LAT / 2) * size;
    const upperRightLensMountCornerLonDelta = +(CAMERA_BOX_GEOJSON_WIDTH_LON / 2) * size * 0.4;

    const upperLeftLensCornerLatDelta = +(CAMERA_BOX_GEOJSON_HEIGHT_LAT / 2) * size * 1.8;
    const upperLeftLensCornerLonDelta = -(CAMERA_BOX_GEOJSON_WIDTH_LON / 2) * size;
    const upperRightLensCornerLatDelta = +(CAMERA_BOX_GEOJSON_HEIGHT_LAT / 2) * size * 1.8;
    const upperRightLensCornerLonDelta = +(CAMERA_BOX_GEOJSON_WIDTH_LON / 2) * size;

    const upperLeftBoxCorner = [
        centerPositionLongitude + upperLeftBoxCornerLonDelta * Math.cos(headingRadians) - upperLeftBoxCornerLatDelta * Math.sin(headingRadians),
        centerPositionLatitude + upperLeftBoxCornerLatDelta * Math.cos(headingRadians) + upperLeftBoxCornerLonDelta * Math.sin(headingRadians),
    ];
    const upperLeftLensMountCorner = [
        centerPositionLongitude + upperLeftLensMountCornerLonDelta * Math.cos(headingRadians) - upperLeftLensMountCornerLatDelta * Math.sin(headingRadians),
        centerPositionLatitude + upperLeftLensMountCornerLatDelta * Math.cos(headingRadians) + upperLeftLensMountCornerLonDelta * Math.sin(headingRadians),
    ];
    const upperLeftLensCorner = [
        centerPositionLongitude + upperLeftLensCornerLonDelta * Math.cos(headingRadians) - upperLeftLensCornerLatDelta * Math.sin(headingRadians),
        centerPositionLatitude + upperLeftLensCornerLatDelta * Math.cos(headingRadians) + upperLeftLensCornerLonDelta * Math.sin(headingRadians),
    ];
    const upperRightLensCorner = [
        centerPositionLongitude + upperRightLensCornerLonDelta * Math.cos(headingRadians) - upperRightLensCornerLatDelta * Math.sin(headingRadians),
        centerPositionLatitude + upperRightLensCornerLatDelta * Math.cos(headingRadians) + upperRightLensCornerLonDelta * Math.sin(headingRadians),
    ];
    const upperRightLensMountCorner = [
        centerPositionLongitude + upperRightLensMountCornerLonDelta * Math.cos(headingRadians) - upperRightLensMountCornerLatDelta * Math.sin(headingRadians),
        centerPositionLatitude + upperRightLensMountCornerLatDelta * Math.cos(headingRadians) + upperRightLensMountCornerLonDelta * Math.sin(headingRadians),
    ];
    const upperRightBoxCorner = [
        centerPositionLongitude + upperRightBoxCornerLonDelta * Math.cos(headingRadians) - upperRightBoxCornerLatDelta * Math.sin(headingRadians),
        centerPositionLatitude + upperRightBoxCornerLatDelta * Math.cos(headingRadians) + upperRightBoxCornerLonDelta * Math.sin(headingRadians),
    ];
    const lowerRightBoxCorner = [
        centerPositionLongitude + lowerRightBoxCornerLonDelta * Math.cos(headingRadians) - lowerRightBoxCornerLatDelta * Math.sin(headingRadians),
        centerPositionLatitude + lowerRightBoxCornerLatDelta * Math.cos(headingRadians) + lowerRightBoxCornerLonDelta * Math.sin(headingRadians),
    ];
    const lowerLeftBoxCorner = [
        centerPositionLongitude + lowerLeftBoxCornerLonDelta * Math.cos(headingRadians) - lowerLeftBoxCornerLatDelta * Math.sin(headingRadians),
        centerPositionLatitude + lowerLeftBoxCornerLatDelta * Math.cos(headingRadians) + lowerLeftBoxCornerLonDelta * Math.sin(headingRadians),
    ];

    const deviceDescription =
        currentDeviceTypeBeingEdited === "edge-device"
            ? `Edge Device: ${deviceInfo.edge_device_id}`
            : currentDeviceTypeBeingEdited === "weather-sensor"
            ? `Weather Sensor: ${deviceInfo.device_sn}`
            : "";

    return {
        type: "Feature",
        properties: makeClickable
            ? {
                  description: deviceDescription,
                  latitude: centerPositionLatitude,
                  longitude: centerPositionLongitude,
              }
            : {},
        geometry: {
            coordinates: [
                [
                    upperLeftBoxCorner,
                    upperLeftLensMountCorner,
                    upperLeftLensCorner,
                    upperRightLensCorner,
                    upperRightLensMountCorner,
                    upperRightBoxCorner,
                    lowerRightBoxCorner,
                    lowerLeftBoxCorner,
                    upperLeftBoxCorner,
                ],
            ],
            type: "Polygon",
        },
    };
}

export default function DeviceLinkingMap({ targetPosition, setTargetPosition, satelliteView }) {
    const dispatch = useAppDispatch();

    const existingDeviceIdBeingEdited = useAppSelector(selectExistingDeviceIdBeingEdited);
    const currentDeviceTypeBeingEdited = useAppSelector(selectCurrentDeviceTypeBeingEdited);
    const currentDeviceBeingEdited = useAppSelector(selectCurrentDeviceBeingEdited);
    const visibleSpans = useAppSelector(selectVisibleSpans);
    const deviceDataset = useAppSelector(selectDeviceData);
    const clientAssetData = useAppSelector(selectClientAssetData);

    const [visibleSpansGeoJSON, setVisibleSpansGeoJSON] = useState(null);
    const [edgeDeviceDataGeoJSON, setEdgeDeviceDataGeoJSON] = useState(null);
    const [weatherSensorDataGeoJSON, setWeatherSensorDataGeoJSON] = useState(null);

    const onClick = useCallback(
        (event) => {
            if (event.features && event.features.length >= 1) {
                const clickedFeature = event.features[0];
                const description = clickedFeature.properties.description;

                if (description && description.startsWith("Structure: ") && currentDeviceBeingEdited && currentDeviceTypeBeingEdited === "edge-device") {
                    const structureName = description.replace("Structure: ", "");
                    dispatch(setTargetStructureForDevice(structureName));
                    return;
                }

                setTargetPosition({
                    latitude: clickedFeature.properties.latitude,
                    longitude: clickedFeature.properties.longitude,
                });
            } else {
                setTargetPosition({ latitude: event.lngLat.lat, longitude: event.lngLat.lng });
            }
        },
        [currentDeviceBeingEdited, currentDeviceTypeBeingEdited, dispatch, setTargetPosition]
    );

    useEffect(() => {
        if (!clientAssetData || !visibleSpans) {
            setVisibleSpansGeoJSON(null);
            return;
        }

        const { spans, structures } = clientAssetData.data;
        const isVisible = (span) =>
            visibleSpans.find(
                (vs) =>
                    (vs.start_structure === span.start_structure &&
                        vs.end_structure === span.end_structure &&
                        vs.section_name === span.section_name) ||
                    (vs.start_structure === span.end_structure &&
                        vs.end_structure === span.start_structure &&
                        vs.section_name === span.section_name)
            );

        setVisibleSpansGeoJSON({
            type: "FeatureCollection",
            features: spans.filter(isVisible).map((span) => generateSpanFeature(span, structures)).filter(Boolean),
        });
    }, [clientAssetData, visibleSpans]);

    useEffect(() => {
        setEdgeDeviceDataGeoJSON({
            type: "FeatureCollection",
            features: [
                ...(deviceDataset?.data?.devices
                    ?.filter((device) => device.edge_device_id !== existingDeviceIdBeingEdited)
                    .map((device) => generateGeoJsonCamera(device, "edge-device", 0.7, true)) || []),
                ...(currentDeviceBeingEdited !== undefined && currentDeviceTypeBeingEdited === "edge-device"
                    ? [generateGeoJsonCamera(currentDeviceBeingEdited, currentDeviceTypeBeingEdited, 0.7, false)]
                    : []),
            ].filter((geoJsonData) => geoJsonData !== null),
        });

        setWeatherSensorDataGeoJSON({
            type: "FeatureCollection",
            features: [
                ...(deviceDataset?.data?.weather_sensors
                    ?.filter((weather_sensor) => weather_sensor.device_sn !== existingDeviceIdBeingEdited)
                    .map((weather_sensor) => generateGeoJsonArrow(weather_sensor, "weather-sensor", 0.7, true)) || []),
                ...(currentDeviceBeingEdited !== undefined && currentDeviceTypeBeingEdited === "weather-sensor"
                    ? [generateGeoJsonArrow(currentDeviceBeingEdited, currentDeviceTypeBeingEdited, 0.7, false)]
                    : []),
            ].filter((geoJsonData) => geoJsonData !== null),
        });
    }, [currentDeviceBeingEdited, currentDeviceTypeBeingEdited, deviceDataset, existingDeviceIdBeingEdited]);

    return (
        <ClientAssetDataMap
            clientAssetData={clientAssetData}
            center={targetPosition}
            satelliteView={satelliteView}
            interactiveLayerIds={["edge-device-data-layer", "weather-sensor-data-layer"]}
            onClick={onClick}
            style={{ width: "100%", height: "100%" }}
        >
            <Source id="visible-spans" type="geojson" data={visibleSpansGeoJSON}>
                <Layer
                    id="visible-span-layer"
                    type="line"
                    paint={{
                        "line-color": "#0000FF",
                        "line-width": 3.5,
                    }}
                />
            </Source>
            <Source id="edge-device-data" type="geojson" data={edgeDeviceDataGeoJSON}>
                <Layer
                    id="edge-device-data-layer"
                    type="fill"
                    paint={{
                        "fill-outline-color": "#000000",
                        "fill-opacity": 0.4,
                        "fill-color": "#555555",
                    }}
                />
            </Source>
            <Source id="weather-sensor-data" type="geojson" data={weatherSensorDataGeoJSON}>
                <Layer
                    id="weather-sensor-data-layer"
                    type="fill"
                    paint={{
                        "fill-outline-color": "#0066CC",
                        "fill-opacity": 0.6,
                        "fill-color": "#00AAFF",
                    }}
                />
            </Source>
            <Marker
                latitude={targetPosition.latitude}
                longitude={targetPosition.longitude}
                offsetLeft={-20}
                offsetTop={-10}
                rotation={0}
            />
        </ClientAssetDataMap>
    );
}
